import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { getSDK } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';
import type {
  AccountSkill,
  AccountSkillMeta,
  CreateAccountSkillRequest,
  ListAccountSkillsResponse,
  UpdateAccountSkillRequest,
} from '@localagent/sdk';

const SKILLS_KEY = ['account-skills'] as const;

function setSkillList(
  queryClient: QueryClient,
  update: (prev: AccountSkillMeta[]) => AccountSkillMeta[],
): void {
  queryClient.setQueryData<ListAccountSkillsResponse>(SKILLS_KEY, (prev) => {
    const skills = update(prev?.skills ?? []).sort((a, b) => a.name.localeCompare(b.name));
    return { skills, count: skills.length };
  });
}

function toMeta(row: AccountSkill): AccountSkillMeta {
  return {
    id: row.id,
    organization_id: row.organization_id,
    account_id: row.account_id,
    name: row.name,
    description: row.description,
    created_by_user_id: row.created_by_user_id,
    updated_by_user_id: row.updated_by_user_id,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

export function useAccountSkills() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  return useQuery({
    queryKey: SKILLS_KEY,
    queryFn: () => getSDK().skills.list(),
    enabled: isAuthenticated && sdkConfigured,
  });
}

export function useCreateAccountSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateAccountSkillRequest) => getSDK().skills.create(req),
    onSuccess: (row) => {
      setSkillList(queryClient, (prev) => [...prev.filter((s) => s.id !== row.id), toMeta(row)]);
    },
  });
}

export function useUpdateAccountSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ skillId, req }: { skillId: string; req: UpdateAccountSkillRequest }) =>
      getSDK().skills.update(skillId, req),
    onSuccess: (row) => {
      setSkillList(queryClient, (prev) => [...prev.filter((s) => s.id !== row.id), toMeta(row)]);
    },
  });
}

export function useDeleteAccountSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (skillId: string) => getSDK().skills.delete(skillId),
    onSuccess: (_res, skillId) => {
      setSkillList(queryClient, (prev) => prev.filter((s) => s.id !== skillId));
    },
  });
}
